import React, { Component } from 'react';
import Downshift from 'downshift';
import withStyles from '@mui/styles/withStyles';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import { CloseIcon, SearchIcon } from '../IconIndex';
import ValidationPlaceholder from './ValidationPlaceholder';
import TextField from './TextField';
import { getDownshiftInputProps, isNullOrUndefined } from '../../utils/objects';

class AutoComplete extends Component {
    static defaultProps = {
        allowUnlisted: false,
        clearOnSelect: false,
        limit: 10,
        minChars: 1
    };

    state = {
        focused: false,
        options: [], // list of searchable options, typically [{label, value|key},]
        searchTerm: '', // what user types in
        searchField: undefined, // the property of "options" to match against the searchTerm (typically "label")
        isLoaded: false
    };

    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
    }

    static getDerivedStateFromProps(newProps, oldState) {
        const { name, form, value, searchField = 'label', options = [] } = newProps;
        const { isLoaded } = oldState;
        if (!isLoaded && ((form && !form.loading) || !form)) {
            // set the available options & initial search term
            const newValue = value || (form ? form.getField(name) : null) || '';
            const newSearchTerm = (newValue ? newValue[searchField] || newValue : null) || '';
            const searchTerm = options.find(opt => newSearchTerm === (opt.key || opt.value));
            return {
                isLoaded: true,
                searchField,
                options,
                searchTerm: searchTerm ? searchTerm[searchField] : newValue
            };
        }
        if (!form && JSON.stringify(options) !== JSON.stringify(oldState.options)) {
            return { options };
        }
        return null;
    }

    onSearchFieldFocus = e => {
        if (!!this.inputRef) this.inputRef.focus();
        if (this.state.focused) return;

        this.setState({ focused: true });
    };

    onSearchFieldBlur = () => {
        const { searchTerm, focused } = this.state;
        if (!focused) return;

        const { name, form, allowUnlisted } = this.props;
        if (form && form.getField(name) !== searchTerm) {
            const match = this.getMatchingItem();
            if (match || allowUnlisted) this.selectItem(match || { value: searchTerm, label: searchTerm });
            else if (!allowUnlisted) this.setState({ searchTerm: '' });
        } else {
            this.setState({ focused: false });
        }
    };

    onSearchFieldRef = input => {
        this.inputRef = input;
    };

    onSearchFieldKeyPress = (e, highlightedIndex, currentOptions) => {
        if (['Enter', 'Tab'].includes(e.key)) {
            if (highlightedIndex === null) {
                const { searchTerm } = this.state;
                const { allowUnlisted } = this.props;
                const match = this.getMatchingItem();
                if (match || allowUnlisted) this.selectItem(match || { value: searchTerm });
                else e.preventDefault();
            } else {
                const nearest = currentOptions[highlightedIndex];
                if (nearest) this.selectItem(nearest);
                else e.preventDefault();
            }
        }
    };

    onSearchFieldChange = e => {
        this.onSearchFieldFocus();
        this.setState({ searchTerm: e.target.value });
    };

    selectItem = item => {
        const { clearOnSelect } = this.props;
        const { searchField } = this.state;
        const key = item ? ('key' in item ? item['key'] : 'value' in item ? item['value'] : item) : null;
        const label = item ? item[searchField] : '';
        const { onChange, name, form } = this.props;
        this.setState({ searchTerm: clearOnSelect ? '' : label, focused: false }, () => {
            if (form) form.setField({ [name]: key });
            if (onChange) onChange(item || {});
        });
    };

    getMatchingItem = () => {
        const { options, searchTerm, searchField } = this.state;
        if (isNullOrUndefined(searchTerm)) return null;
        const upperSearchTerm = searchTerm.toUpperCase();
        return options.find(x => x[searchField].toUpperCase() === upperSearchTerm);
    };

    render() {
        const props = {
            ...this.props,
            onItemSelected: this.selectItem,
            onSearchFieldChange: this.onSearchFieldChange,
            onSearchFieldKeyPress: this.onSearchFieldKeyPress,
            onSearchFieldFocus: this.onSearchFieldFocus,
            onSearchFieldBlur: this.onSearchFieldBlur,
            onSearchFieldRef: this.onSearchFieldRef
        };
        return <Downshift>{downshiftData => this.renderContainer(props, downshiftData)}</Downshift>;
    }

    renderContainer = (props, data) => {
        const { searchTerm, focused, options, searchField } = this.state;
        const {
            form,
            name,
            classes,
            className,
            onItemSelected,
            onSearchFieldRef,
            allowUnlisted,
            minChars,
            limit
        } = props;
        const { maxHeight } = this.props;
        const { highlightedIndex } = data;
        const validOptions =
            options && Array.isArray(options)
                ? options.filter(
                    item => !searchTerm || item[searchField].toLowerCase().includes(searchTerm.toLowerCase())
                )
                : [];
        const s = validOptions.length - limit === 1;
        const isSearching = !(!focused || !searchTerm || (isNaN(searchTerm) && searchTerm.length < minChars));

        return (
            <div className={`autocomplete ${className || ''}`}>
                <ValidationPlaceholder form={form} name={name}>
                    <div style={{ padding: 0, position: 'relative' }}>
                        <SearchField
                            {...props}
                            ref={onSearchFieldRef}
                            currentOptions={validOptions}
                            data={data}
                            searchTerm={searchTerm}
                            highlightedIndex={highlightedIndex}
                        />
                        {isSearching && (
                            <Paper className="hover-search-results" square style={{ maxHeight }}>
                                {validOptions.length > 0 ? (
                                    validOptions
                                        .filter((item, index) => index < limit)
                                        .map((item, index) => (
                                            <SearchResult
                                                key={index}
                                                {...props}
                                                data={data}
                                                selectItem={onItemSelected}
                                                item={item}
                                                index={index}
                                                searchField={searchField}
                                            />
                                        ))
                                ) : (
                                    <div className={classes.noResult}>
                                        <span className={classes.itemLabel}>No results, {!allowUnlisted
                                            ? <>try a different search?</>
                                            : <>use this anyway?</>}</span>
                                    </div>
                                )}
                                {validOptions.length > limit && (
                                    <div className={classes.noResult}>
                                    <span className={classes.itemLabel}>
                                        There {!s ? 'are' : 'is'} {validOptions.length - limit} more result
                                        {!s ? 's' : ''}, refine your search?
                                    </span>
                                    </div>
                                )}
                            </Paper>
                        )}
                    </div>
                </ValidationPlaceholder>
            </div>
        );
    };
}

const SearchField = React.forwardRef(
    (
        {
            open,
            label,
            placeholder,
            onSearchFieldChange,
            onSearchFieldKeyPress,
            onSearchFieldFocus,
            onSearchFieldBlur,
            onItemSelected,
            currentOptions,
            data,
            searchTerm,
            highlightedIndex,
            disabled,
            required,
            form,
            name
        },
        onSearchFieldRef
    ) => {
        const downshiftInputProps = getDownshiftInputProps(data.getInputProps);
        // This is to shut up linting errors because it's case-insensitive
        const otherProps = {
            required,
            InputProps: {
                tabIndex: open ? 0 : -1,
                disableUnderline: true,
                inputRef: onSearchFieldRef,
                disabled: disabled,
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon className="input-search" />
                    </InputAdornment>
                ),
                endAdornment: searchTerm && !disabled && (
                    <InputAdornment position="end">
                        <span title="Remove">
                            <CloseIcon
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: 'grey',
                                    padding: 1,
                                    fontSize: 12,
                                    borderRadius: 36,
                                    color: 'white'
                                }}
                                onClick={() => {
                                    onSearchFieldChange({ target: { value: '' } });
                                    onItemSelected(null);
                                }}
                            />
                        </span>
                    </InputAdornment>
                ),
                ...downshiftInputProps({
                    placeholder,
                    onFocus: onSearchFieldFocus,
                    onBlur: onSearchFieldBlur,
                    onChange: onSearchFieldChange,
                    onKeyDown: e => onSearchFieldKeyPress(e, highlightedIndex, currentOptions),
                    onClick: () => onSearchFieldFocus(),
                    value: searchTerm
                })
            }
        };
        return <TextField name="searchTerm" label={label} fullWidth {...otherProps} />;
    }
);

const SearchResult = ({ data, selectItem, item, index, searchField }) => {
    const key = item.key || item.value;
    const label = item[searchField];
    const itemProps = data.getItemProps({ item: key });
    return (
        <MenuItem
            {...itemProps}
            className="item"
            selected={data.highlightedIndex === index}
            onClick={() => selectItem(item)}
        >
            <span className="item-label">{label}</span>
        </MenuItem>
    );
};

const styles = () => ({
    root: {
        position: 'relative',
        flex: '1 1 auto'
    },
    noResult: {
        color: '#757575',
        padding: 16,
        '&:not(:first-child)': {
            borderTop: '1px solid #d5d5d5',
            padding: '8px 16px 0',
            textAlign: 'right'
        }
    }
});
export default withStyles(styles)(AutoComplete);
